import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.tsx'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { SheetDescription } from '@/components/ui/sheet.tsx'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import LingoFlowIcon from '@/components/LingoFlowIcon.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { AlertCircle, Check, CheckCircle2 } from 'lucide-react'
import ShineBorder from '@/components/ui/shine-border.tsx'
import { Purchases, PURCHASES_ERROR_CODE, PurchasesOffering, PurchasesPackage } from '@revenuecat/purchases-capacitor'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx'
import { toast } from 'sonner'
import { LoadingButton } from '@/components/loading-button.tsx'
import api from '@/api.ts'
import { User } from '@/models.ts'
import useGlobalStore from '@/useGlobalStore.ts'
import { Capacitor } from '@capacitor/core'

interface AuthDialogProps {
	open: boolean
	dismissible: boolean | undefined
	onDismissed: (dismissed: boolean) => void
	mode?: string
	actionMessage?: string | null
	appCurrentOffering: PurchasesOffering | null
	onSuccessfulPurchase: () => void
}

// const calculateDiscount = (offer1Price: number, offer2Price: number): number => {
// 	return (1 - offer2Price / offer1Price) * 100
// }

const formatCurrency = (value: string): string => {
	return value.replace(/([A-Z]+)(\d)/g, '$1 $2').replace(/(\d)([A-Z]+)/g, '$1 $2')
}

const PurchaseSubscriptionDialog = ({
	open,
	dismissible = true,
	onDismissed,
	mode = 'basic',
	actionMessage,
	appCurrentOffering,
	onSuccessfulPurchase,
}: AuthDialogProps) => {
	const { t } = useTranslation()
	const { user, setUser, availableAppLanguages, appLanguageCode } = useGlobalStore()
	const [selectedPackage, setSelectedPackage] = useState<PurchasesPackage | null>(null)
	const [purchaseError, setPurchaseError] = useState<string | null>(null)
	const [isPurchaseLoading, setIsPurchaseLoading] = useState(false)

	const handleInteractionOutside = () => {
		if (dismissible) {
			onDismissed(true)
		}
	}

	useEffect(() => {
		if (open) {
			setPurchaseError(null)
		}
	}, [open])

	const makePurchase = (offerPackage: PurchasesPackage) => {
		setPurchaseError(null)
		setIsPurchaseLoading(true)
		try {
			Purchases.purchasePackage({ aPackage: offerPackage })
				.then((purchaseResult) => {
					if (Object.keys(purchaseResult.customerInfo.entitlements.active).length > 0) {
						setPurchaseError(null)
						setIsPurchaseLoading(true)
						setTimeout(() => {
							api('/v1/user?refresh_subscriptions=true')
								.then((data: User) => {
									const dataCopy = JSON.parse(JSON.stringify(data)) as User
									dataCopy.access_token = user?.access_token ?? ''
									setUser(dataCopy)
									onSuccessfulPurchase()
								})
								.catch((error) => {
									toast(error.message)
								})
								.finally(() => {
									// @ts-ignore
									window.tempAppAuthToken = null
									setIsPurchaseLoading(false)
								})
						}, 1000)
					}
				})
				.catch((error: any) => {
					setPurchaseError(error.message)
					setIsPurchaseLoading(false)
				})
		} catch (error: any) {
			setIsPurchaseLoading(false)
			console.error('error making purchase', JSON.stringify(error))
			if (error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
				toast('Subscription purchase canceled')
			} else {
				setPurchaseError(error.message)
			}
		}
	}

	return (
		<Dialog open={open}>
			<DialogContent
				dir={availableAppLanguages[appLanguageCode]?.dir}
				className={`px-6 overflow-y-auto rounded-lg ${mode === 'maximum' ? 'h-full py-4' : 'py-6'}`}
				hideClose={true}
				onInteractOutside={handleInteractionOutside}>
				<VisuallyHidden.Root>
					<DialogTitle>{t('Buy a subscription')}</DialogTitle>
					<SheetDescription>{t('Upgrade to an Unlimited subscription')}</SheetDescription>
				</VisuallyHidden.Root>
				<div className="flex flex-col gap-3 justify-center">
					{mode === 'maximum' && (
						<div className="rounded-lg p-3">
							<Badge variant="secondary">{t('Free Account')}</Badge>
							<div className="flex flex-col gap-1 mt-3">
								<div>
									<div className="flex items-center">
										<Check className="text-yellow-500 w-4 h-4 flex-shrink-0" />
										<span className="ms-2 text-sm">{t('AI chats + learning extractions')}</span>
									</div>
									<div className="flex flex-col">
										<small className="ms-6 text-xs">{t('Limited')}: 10</small>
									</div>
								</div>
								<div>
									<div className="flex items-center">
										<Check className="text-yellow-500 w-4 h-4 flex-shrink-0" />
										<span className="ms-2 text-sm">{t('Vocabulary library')}</span>
									</div>
									<div className="flex flex-col">
										<small className="ms-6 text-xs">{t('Limited')}: 10</small>
									</div>
								</div>
								<div>
									<div className="flex items-center">
										<Check className="text-yellow-500 w-4 h-4 flex-shrink-0" />
										<span className="ms-2 text-sm">{t('Vocabulary practice')}</span>
									</div>
									<div className="flex flex-col">
										<small className="ms-6 text-xs">
											{t('Limited')}: 1 / {t('Day')}
										</small>
									</div>
								</div>
							</div>
						</div>
					)}
					<div>
						<div className="text-base text-orange-500 text-center">{actionMessage}</div>
						<div className="text-base mt-3">
							{t('Upgrade to Unlimited Access to remove all limits and access more features!')}
						</div>
					</div>
					<ShineBorder
						className="w-full flex flex-col items-start rounded-lg p-3 bg-secondary"
						color={['#A07CFE', '#FE8FB5', '#FFBE7B']}
						duration={20}>
						<div className="flex items-center">
							<LingoFlowIcon className="w-5 h-5 me-2" />
							<Badge className="flex justify-center min-h-7 rounded-md text-sm font-bold flex-col diagonal-stripes border-0 text-black">
								{t('Unlimited Access')}
							</Badge>
						</div>
						<div className="w-full flex flex-col gap-1 mt-3">
							<div>
								<div className="flex items-center">
									<Check className="text-green-500 w-4 h-4 flex-shrink-0" />
									<span className="ms-2 text-base">
										{t('Better AI chats + learning extractions')}
									</span>
								</div>
								{/*<div className="flex items-center ms-6">*/}
								{/*	<Plus className="w-3 h-3 text-green-500" />*/}
								{/*	<small className="ms-1 text-xs">{t('Unlimited AI chats')}</small>*/}
								{/*</div>*/}
							</div>
							<div>
								<div className="flex items-center">
									<Check className="text-green-500 w-4 h-4 flex-shrink-0" />
									<span className="ms-2 text-base">{t('Vocabulary library')}</span>
								</div>
								{/*<div className="flex flex-col">*/}
								{/*	<div className="ms-6 flex items-center">*/}
								{/*		<Plus className="w-3 h-3 text-green-500" />*/}
								{/*		<small className="ms-1 text-xs">{t('Unlimited saved vocabulary')}</small>*/}
								{/*	</div>*/}
								{/*	<div className="ms-6 flex items-center">*/}
								{/*		<Plus className="w-3 h-3 text-green-500" />*/}
								{/*		<small className="ms-1 text-xs">{t('AI translator')}</small>*/}
								{/*	</div>*/}
								{/*</div>*/}
							</div>
							<div>
								<div className="flex items-center">
									<Check className="dark:text-green-500 text-green-600 w-4 h-4 flex-shrink-0" />
									<span className="ms-2 text-base">{t('Vocabulary practice')}</span>
								</div>
								{/*<div className="flex items-center ms-6">*/}
								{/*	<Plus className="w-3 h-3 text-green-500" />*/}
								{/*	<small className="ms-1 text-xs">{t('Unlimited vocabulary practice')}</small>*/}
								{/*</div>*/}
							</div>
						</div>
						<div className="w-full flex flex-col gap-3 mt-4">
							{appCurrentOffering?.availablePackages?.map((offerPackage) => (
								<div
									key={offerPackage.product.identifier}
									className={`dark:bg-accent bg-gray-300 rounded-md ${selectedPackage?.product.identifier === offerPackage.product.identifier ? 'border dark:border-white border-black shadow' : ''}`}
									onClick={() => setSelectedPackage(offerPackage)}>
									<div className="px-3 py-2">
										<div className="flex items-center justify-between">
											<span className="font-bold">{t(offerPackage.identifier)}</span>
											{selectedPackage?.product.identifier ===
												offerPackage.product.identifier && (
												<CheckCircle2 className="dark:text-green-500 text-green-600 w-5 h-5 flex-shrink-0" />
											)}
										</div>
										<div className="flex justify-between items-center">
											<div
												className={`${['ios', 'android'].includes(Capacitor.getPlatform()) ? 'font-semibold' : 'text-xs font-light'}`}>
												{offerPackage.product.priceString}
											</div>
											{offerPackage.product.pricePerMonthString !== null && (
												<div>
													<span
														className={`${['ios', 'android'].includes(Capacitor.getPlatform()) ? 'font-light mx-2' : 'font-semibold mx-2'}`}>
														{formatCurrency(offerPackage.product.pricePerMonthString)}
													</span>
													<span className="text-xs">/ {t('mo')}</span>
												</div>
											)}
										</div>
									</div>
								</div>
							))}
							<div className="flex flex-col items-center justify-center">
								<div className="text-xs">
									<span className="me-1">{t('Payment provider')}:</span>
									{Capacitor.getPlatform() === 'ios'
										? 'App Store'
										: Capacitor.getPlatform() === 'android'
											? 'Google Play'
											: '-'}
								</div>
								<div className="text-sm">{t('Recurring billing, cancel anytime.')}</div>
							</div>
							{purchaseError !== null && (
								<div>
									<Alert variant="destructive">
										<AlertCircle className="h-4 w-4" />
										<AlertTitle>{t('Failed to purchase a subscription')}</AlertTitle>
										<AlertDescription className="dark:text-white text-black">
											{purchaseError === '' ? (
												<>
													{t('Try again or contact support:')}
													<a
														className="ms-1 underline underline-offset-4"
														href="mailto:support@lingoflow.app">
														support@lingoflow.app
													</a>
												</>
											) : (
												<span>{t(purchaseError)}</span>
											)}
										</AlertDescription>
									</Alert>
								</div>
							)}
							{appCurrentOffering?.availablePackages?.length !== undefined &&
							appCurrentOffering?.availablePackages?.length > 0 ? (
								<LoadingButton
									loading={isPurchaseLoading}
									onClick={() => makePurchase(selectedPackage!)}
									className="w-full text-md active:bg-secondary-foreground">
									{t('Continue')}
								</LoadingButton>
							) : (
								<div className="text-center">
									{Capacitor.getPlatform() === 'web' ? (
										<div className="text-sm text-orange-400">
											Purchasing a subscription is only possible in the LingoFlow mobile app.
											Visit
											<a
												className="text-blue-400 mx-1"
												href="https://lingoflow.app"
												target="_blank">
												lingoflow.app
											</a>
											and download the app for iOS or Android.
										</div>
									) : (
										t('NO OFFERS AVAILABLE')
									)}
								</div>
							)}
						</div>
					</ShineBorder>
				</div>
				<div className="text-xs">
					{t(
						'Subscription automatically renews for the same length term unless you cancel before it is renewed. You keep your unlimited access for the period you paid for.',
					)}
				</div>
				{dismissible && (
					<div className="flex justify-center items-center text-sm text-gray-400">
						<span className="cursor-pointer" onClick={() => onDismissed(true)}>
							{t('Dismiss')}
						</span>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default PurchaseSubscriptionDialog
